import { ReactNode } from "react";
import { useCommercialMode } from "@/hooks/useCommercialMode";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";

interface FreePriceTagProps {
  /** Le prix normal à afficher (sera barré en mode gratuit). */
  children: ReactNode;
  /** Variante d'affichage. */
  variant?: "inline" | "stacked";
  /** Cacher le badge "Offert actuellement". */
  hideBadge?: boolean;
  className?: string;
}

/**
 * Affiche un prix. En "Mode Gratuit" piloté par l'admin :
 *  - le prix est automatiquement barré
 *  - un badge vert "Offert actuellement" apparaît
 */
const FreePriceTag = ({
  children,
  variant = "stacked",
  hideBadge = false,
  className = "",
}: FreePriceTagProps) => {
  const { freeMode, freeLabel } = useCommercialMode();

  if (!freeMode) {
    return <span className={className}>{children}</span>;
  }

  if (variant === "inline") {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <span className="line-through text-muted-foreground opacity-70">{children}</span>
        {!hideBadge && (
          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/20">
            <Sparkles className="w-3 h-3 mr-1" /> {freeLabel}
          </Badge>
        )}
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col items-center gap-1 ${className}`}>
      <span className="line-through text-muted-foreground opacity-70">{children}</span>
      {!hideBadge && (
        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/20">
          <Sparkles className="w-3 h-3 mr-1" /> {freeLabel}
        </Badge>
      )}
    </span>
  );
};

export default FreePriceTag;
